import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

// Custom encoder for HttpParams (handles special characters)
class CustomHttpParamEncoder {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

@Injectable({
  providedIn: 'root'
})
export class F1065SchBService {
  
  constructor(private http: HttpClient) {}

  // Load main form data via pivot form
  loadFormData(baseUrl: string, globalParams: any, rowData: any): Observable<any> {
    const params: any = {
      action_id: '31259',
      tax_year: (rowData.TAX_YEAR ?? globalParams.tax_year ?? '').toString(),
      scenario: (rowData.SCENARIO_KEY ?? globalParams.scenario ?? '').toString(),
      jcd_key: (rowData.JCD_KEY ?? globalParams.jcd_key ?? '').toString(),
      combination_key: (rowData.COMBINATION_KEY ?? '').toString(),
      schedule_name: 'B',
      part_no: 'NA',
      activity_key: globalParams.activity_key ?? '',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
    };
    return this.http.get<any>(`${baseUrl}/loadPivotFormJson`, { params });
  }

  // Load sub-table records
  loadSubTableData(baseUrl: string, globalParams: any, rowData: any): Observable<any> {
    const params: any = {
      action_code: 'z57kmr',
      combination_key: (rowData.COMBINATION_KEY ?? '').toString(),
      schedule: 'B',
      activity_key: globalParams.activity_key ?? '',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
      tax_year: rowData.TAX_YEAR ?? globalParams.tax_year ?? '',
    };
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params });
  }

  // Load country list
  loadCountryList(baseUrl: string, globalParams: any, rowData: any): Observable<any> {
    const params: any = {
      action_code: 'fyl404',
      activity_key: globalParams.activity_key ?? '',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
      tax_year: rowData.TAX_YEAR ?? globalParams.tax_year ?? '',
    };
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params });
  }

  // Load US state list
  loadUSStateList(baseUrl: string, globalParams: any, rowData: any): Observable<any> {
    const params: any = {
      action_code: 'oppj8b',
      activity_key: globalParams.activity_key ?? '',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
      tax_year: rowData.TAX_YEAR ?? globalParams.tax_year ?? '',
    };
    return this.http.get<any>(`${baseUrl}/loadJsonObject`, { params });
  }

  // Save form data
  saveScheduleBForm(
    baseUrl: string,
    formData: any[],
    rowData: any,
    filterParams: any,
    globalParams: any
  ): Observable<any> {
    const jsonSettings = {
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO_KEY,
      jcd_key: rowData.JCD_KEY,
      combination_key: rowData.COMBINATION_KEY,
      trans_type_key: formData[0]?.editrows?.[0]?.[0]?.trans_type_key || ''
    };

    const jsonObj = JSON.stringify(formData);
    const jsonSettingsStr = JSON.stringify(jsonSettings);

    // Build form data as URL encoded params
    let httpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    
    // Add global params first
    if (globalParams) {
      Object.keys(globalParams).forEach(key => {
        if (globalParams[key] !== undefined && globalParams[key] !== null) {
          httpParams = httpParams.set(key, globalParams[key].toString());
        }
      });
    }
    
    // Add filter params
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null) {
          httpParams = httpParams.set(key, filterParams[key].toString());
        }
      });
    }
    
    // Add required params
    httpParams = httpParams.set('jsonObj', jsonObj);
    httpParams = httpParams.set('jsonSettings', jsonSettingsStr);
    httpParams = httpParams.set('combination_key', rowData.COMBINATION_KEY.toString());
    httpParams = httpParams.set('trans_type_key', formData[0]?.editrows?.[0]?.[0]?.trans_type_key?.toString() || '');

    httpParams = httpParams.set('process_name', globalParams.process_name || '1065 Textual Information');
    httpParams = httpParams.set('checkLockedbj', JSON.stringify([{
      leid: rowData.LEID,
      combination_key: rowData.COMBINATION_KEY,
      scenario_key: rowData.SCENARIO_KEY
    }]));

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      `${baseUrl}/saveJsonObject?action_code=tkgaam`,
      httpParams.toString(),
      { headers }
    );
  }
}

